import * as React from 'react';
import { GridHeaderFilterCellProps } from '@mui/x-data-grid-pro/components/headerFiltering/GridHeaderFilterCell';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { FilterContext } from '../DataGrid';
import './MultiSelect.scss';

interface TextFieldFilterProps extends GridHeaderFilterCellProps {
  inputType?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  allowDecimal?: boolean;
}

export const TextFieldFilter: React.FC<TextFieldFilterProps> = ({ item, inputType, allowDecimal }) => {
  const { values, onChange, disabledFields } = React.useContext(FilterContext);
  const isDisabled = disabledFields?.has(item.field) ?? false;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  // Sync external value changes (e.g. filter cleared from outside) back to the DOM.
  React.useEffect(() => {
    const val = values[item.field] ?? '';
    if (inputRef.current && inputRef.current.value !== val) {
      inputRef.current.value = val;
    }
  }, [values[item.field], item.field]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (inputType === 'number' && next !== '' && !(allowDecimal ? /^-?(?:\d+(?:\.\d*)?|\.\d*)?$/ : /^-?\d+$/).test(next)) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(item.field, next || undefined);
    }, 300);
  };

  const handleClear = () => {
    if (inputRef.current) inputRef.current.value = '';
    clearTimeout(timerRef.current);
    onChange(item.field, undefined);
  };

  const hasValue = !!values[item.field];

  return (
    <div className={`filter-row${isDisabled ? ' filter-row--disabled' : ''}`}>
      <div className="text-filter-input">
        <input
          ref={inputRef}
          type={inputType}
          defaultValue={values[item.field] ?? ''}
          onChange={handleChange}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.stopPropagation()}
          placeholder=""
          disabled={isDisabled}
        />
      </div>
      {hasValue && (
        <IconButton size="small" onClick={handleClear} className="filter-clear-btn" tabIndex={-1}>
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

export default TextFieldFilter;
